import React, {
  useCallback,
  useMemo,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { colors } from '@styleseat/brand';
import { Box, Inline } from '@styleseat/ui';
import {
  useIsMobile,
  useLinkTo,
  useParams,
  useRouteName,
} from '../../../../hooks';
import analytics from '../../../../modules/analytics';
import nonCriticalException from '../../../../modules/exceptionLogger';
import { IProviderClient, ProviderClientPhoneType } from '../../../../api/Providers/Clients/types';
import { handleServerErrors } from '../../../../modules/provider/ClientFields';
import { addFirstClient, isFirstClientAdded } from '../../../../modules/provider/ProClientList';
import { getStatusError } from '../../../../modules/statusCode';
import { IRootDispatch } from '../../../../store/models';
import {
  Button,
  ModalBox,
  Stack,
} from '../../../ui';
import { ArrowsArrowLeftIcon } from '../../../ui/Icons';
import FirstClientAddedSnackbar from '../../FirstClientAdded/FirstClientAddedSnackbar';
import { ProSettingsLayout } from '../../settings/ProSettingsLayout';
import { ProClientForm } from '../ProClientForm/ProClientForm';
import { bulkImportSupported, openAppDownloadDialog } from '../../../../modules/provider/Client/importContacts';
import { APP } from '../../../../config';
import { PRO_CLIENT_IMPORT, PRO_CLIENT_LIST } from '../../../../route-names';
import { ReminderType } from '../../../../api/Providers/Appointments';
import { PartialProviderClient } from '../../../../modules/provider/Client';

const DEFAULT_CLIENT_DATA = {
  reminder_preference: ReminderType.SmsEmailReminder,
};

type ProClientAddProps = {
  onSave: (client: IProviderClient) => void;
  onClose: () => void;
};

export const ProClientAdd: React.FC<ProClientAddProps> = ({
  onSave,
  onClose,
}) => {
  const isMobile = useIsMobile();
  const canImportContacts = useMemo(() => (
    isMobile
    || APP
    || bulkImportSupported()
  ), [isMobile]);
  const linkTo = useLinkTo();
  const dispatch = useDispatch<IRootDispatch>();
  const [client, setClient] = useState<PartialProviderClient>(DEFAULT_CLIENT_DATA);
  const [errors, setErrors] = useState<any>();
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [isValid, setIsValid] = useState<boolean>(false);
  const [routeName] = useRouteName();
  const {
    providerId,
    source,
  } = useParams();

  const handleSaveSuccess = useCallback(async newClient => {
    const showFirstClientAddedSnackBar = await isFirstClientAdded();
    const isViewingClientList = routeName === PRO_CLIENT_LIST;
    if (showFirstClientAddedSnackBar && isViewingClientList) {
      FirstClientAddedSnackbar.open([newClient], Number(providerId));
    }

    if (newClient) {
      onSave?.(newClient);
    }

    addFirstClient([newClient]);
  }, [
    providerId,
    routeName,
    onSave,
  ]);

  const handleSave = useCallback(async () => {
    let response;
    if (!submitting) {
      setErrors({});
      setSubmitting(true);
      try {
        analytics.track('P Clicked Save New Client', {
          has_email: !!client.email,
          has_phone_mobile: !!client.phones?.find(phone => (
            phone.type === ProviderClientPhoneType.Mobile
          )),
          has_phone_home: !!client.phones?.find(phone => (
            phone.type === ProviderClientPhoneType.Home
          )),
        });
        const [result] = await dispatch.providerClients.createMultipleClients({
          providerId,
          clients: [client],
        });
        handleSaveSuccess(result);
        analytics.track('PC Added');
        response = 'success';
      } catch (e) {
        response = 'error';
        let errorResult = e;

        nonCriticalException(e);

        if (e?.data) {
          errorResult = e.data;
        } else if (typeof e?.code === 'number') {
          errorResult = getStatusError(e.code);
        }

        setErrors(handleServerErrors(errorResult, 'add'));
      } finally {
        analytics.track('P Clicked Save New Client Response', {
          save_new_client_response: response,
        });
        setSubmitting(false);
      }
    }
  }, [
    client,
    providerId,
    dispatch.providerClients,
    submitting,
    handleSaveSuccess,
  ]);

  const handleOpenContactImportModal = () => {
    onClose?.();
    if (bulkImportSupported()) {
      analytics.track('P Clicked Add New Client', {
        pro_add_clients_method: 'import-from-device-button',
        source,
      });
      linkTo(
        PRO_CLIENT_IMPORT,
        { returnRoute: routeName },
      );
    } else {
      openAppDownloadDialog();
    }
  };

  return (
    <ProSettingsLayout
      title="Add Client"
      leftButtonProps={{
        children: <ArrowsArrowLeftIcon color={colors.ash} width={20} height={20} />,
        onClick: onClose,
        testID: 'ProClientAdd-button--cancel',
      }}
      rightButtonProps={{
        text: submitting ? 'Saving' : 'Save',
        onClick: handleSave,
        disabled: !isValid || submitting,
        testID: 'ProClientAdd-button--save',
      }}
    >
      <ModalBox scrollable padding={16}>
        <Stack space={16}>
          {canImportContacts ? (
            <Box alignItems="center">
              <Inline>
                <Button
                  appearance="link-lowercase"
                  size="medium"
                  onClick={handleOpenContactImportModal}
                  testID="ProClientAdd-button--importContacts"
                >
                  Import from contacts
                </Button>
              </Inline>
            </Box>
          ) : null}
          <ProClientForm
            client={client}
            onChange={setClient}
            errors={errors}
            onValidationChange={setIsValid}
            collapsible
          />
        </Stack>
      </ModalBox>
    </ProSettingsLayout>
  );
};
